import { http, HttpResponse } from 'msw';
import {
  DEMO_BOTS,
  DEMO_BOT_PORTFOLIOS,
  DEMO_BOT_SUMMARIES,
  DEMO_BOT1_EQUITY,
  DEMO_BOT2_EQUITY,
  DEMO_BOT3_EQUITY,
  DEMO_BOT1_PNL,
  DEMO_BOT2_PNL,
  DEMO_BOT3_PNL,
  DEMO_POSITIONS,
  INITIAL_BOT_STATES,
  type DemoBotState,
} from '../demo-fixtures';

const BASE = import.meta.env?.VITE_API_BASE_URL ?? 'http://localhost:8000';

// ── Mutable runtime state (resets when service worker restarts) ───────────────
const botStates: Record<number, DemoBotState> = { ...INITIAL_BOT_STATES };
// Deep copy each state to avoid mutation of the initial values
Object.keys(botStates).forEach((k) => {
  const key = Number(k);
  const initial = INITIAL_BOT_STATES[key];
  if (initial) botStates[key] = { ...initial };
});

// ── Mutable bot list (allows demo bot creation) ───────────────────────────────
// Cast to loose type to avoid complex TypeScript structural typing issues with the
// heterogeneous DEMO_BOTS union (each bot has slightly different config shapes).
let demoBots: Array<Record<string, unknown>> = [...DEMO_BOTS] as Array<Record<string, unknown>>;
let nextBotId = 4;

const BOT_EQUITIES: Record<number, typeof DEMO_BOT1_EQUITY> = {
  1: DEMO_BOT1_EQUITY,
  2: DEMO_BOT2_EQUITY,
  3: DEMO_BOT3_EQUITY,
};

const BOT_PNL: Record<number, typeof DEMO_BOT1_PNL> = {
  1: DEMO_BOT1_PNL,
  2: DEMO_BOT2_PNL,
  3: DEMO_BOT3_PNL,
};

function getBotById(id: number) {
  return demoBots.find((b) => b.id === id);
}

function makeStatusResponse(id: number) {
  const fallback: DemoBotState = {
    runtime_state: 'stopped',
    allowed_actions: ['start', 'delete'],
    cycle_count: 0,
    last_heartbeat_at: null,
    last_error: null,
    restart_count: 0,
    uptime_seconds: 0,
  };
  const state = botStates[id] ?? INITIAL_BOT_STATES[1] ?? fallback;
  const bot = getBotById(id);
  return {
    bot_id: id,
    bot_name: bot?.name ?? `Bot ${id}`,
    runtime_state: state.runtime_state,
    db_status: 'ACTIVE',
    allowed_actions: state.allowed_actions,
    cycle_count: state.cycle_count,
    has_active_orders: state.runtime_state === 'running',
    last_heartbeat_at:
      state.runtime_state === 'running' ? new Date().toISOString() : state.last_heartbeat_at,
    last_error: state.last_error,
    restart_count: state.restart_count,
  };
}

export const demoBotsHandlers = [
  // ── List bots ──────────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/bots`, () => {
    return HttpResponse.json({
      items: demoBots,
      total: demoBots.length,
      limit: 50,
      offset: 0,
    });
  }),

  // ── Create bot ────────────────────────────────────────────────────────────
  http.post(`${BASE}/api/v1/bots`, async ({ request }) => {
    const body = (await request.json()) as Record<string, unknown>;
    const newBot = {
      id: nextBotId++,
      user_id: 42,
      name: (body['name'] as string) ?? 'New Demo Bot',
      status: 'ACTIVE' as const,
      execution_mode: (body['execution_mode'] as 'PAPER' | 'SHADOW') ?? 'PAPER',
      exchange_account_id: 1,
      symbols: (body['symbols'] as string[]) ?? ['BTC/USDT'],
      intervals: (body['intervals'] as string[]) ?? ['1h'],
      strategy_type: (body['strategy_type'] as string) ?? 'RSI_STRATEGY',
      config: (body['config'] as object) ?? {},
      risk_limits: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    demoBots = [...demoBots, newBot];
    botStates[newBot.id] = {
      runtime_state: 'stopped',
      allowed_actions: ['start'],
      cycle_count: 0,
      last_heartbeat_at: null,
      last_error: null,
      restart_count: 0,
      halt_reason: null,
      uptime_seconds: 0,
    };
    return HttpResponse.json(newBot, { status: 201 });
  }),

  // ── Get bot ────────────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/bots/:id`, ({ params }) => {
    const id = Number(params['id']);
    const bot = getBotById(id);
    if (!bot) return HttpResponse.json({ detail: 'Bot not found' }, { status: 404 });
    return HttpResponse.json(bot);
  }),

  // ── Update bot ────────────────────────────────────────────────────────────
  http.patch(`${BASE}/api/v1/bots/:id`, async ({ request, params }) => {
    const id = Number(params['id']);
    const body = (await request.json()) as object;
    demoBots = demoBots.map((b) =>
      b.id === id
        ? { ...b, ...(body as Partial<typeof b>), updated_at: new Date().toISOString() }
        : b,
    );
    return HttpResponse.json(getBotById(id));
  }),

  // ── Delete bot ────────────────────────────────────────────────────────────
  http.delete(`${BASE}/api/v1/bots/:id`, ({ params }) => {
    const id = Number(params['id']);
    demoBots = demoBots.filter((b) => b.id !== id);
    return new HttpResponse(null, { status: 204 });
  }),

  // ── Bot status ────────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/bots/:id/status`, ({ params }) => {
    const id = Number(params['id']);
    return HttpResponse.json(makeStatusResponse(id));
  }),

  // ── Start bot ─────────────────────────────────────────────────────────────
  http.post(`${BASE}/api/v1/bots/:id/start`, ({ params }) => {
    const id = Number(params['id']);
    const state = botStates[id];
    if (state) {
      botStates[id] = {
        ...state,
        runtime_state: 'running',
        allowed_actions: ['stop', 'halt', 'restart'],
        last_heartbeat_at: new Date().toISOString(),
        cycle_count: 0,
        uptime_seconds: 0,
      };
    }
    return HttpResponse.json({
      bot_id: id,
      bot_name: getBotById(id)?.name ?? `Bot ${id}`,
      runtime_state: 'running',
      action: 'start',
      message: 'Bot started in PAPER mode (DEMO)',
    });
  }),

  // ── Stop bot ──────────────────────────────────────────────────────────────
  http.post(`${BASE}/api/v1/bots/:id/stop`, ({ params }) => {
    const id = Number(params['id']);
    const state = botStates[id];
    if (state) {
      botStates[id] = {
        ...state,
        runtime_state: 'stopped',
        allowed_actions: ['start'],
        last_heartbeat_at: null,
      };
    }
    return HttpResponse.json({
      bot_id: id,
      bot_name: getBotById(id)?.name ?? `Bot ${id}`,
      runtime_state: 'stopped',
      action: 'stop',
      message: 'Bot stopped (DEMO)',
    });
  }),

  // ── Halt bot ──────────────────────────────────────────────────────────────
  http.post(`${BASE}/api/v1/bots/:id/halt`, async ({ request, params }) => {
    const id = Number(params['id']);
    const body = (await request.json()) as Record<string, string>;
    const reason = body['reason'] ?? 'Manual halt';
    const state = botStates[id];
    if (state) {
      botStates[id] = {
        ...state,
        runtime_state: 'halted',
        allowed_actions: ['resume'],
        halt_reason: reason,
        last_heartbeat_at: new Date().toISOString(),
      };
    }
    return HttpResponse.json({
      bot_id: id,
      action: 'halt',
      status: 'HALTED',
      reason,
      actor: `user:${42}`,
      halted_at: new Date().toISOString(),
      message: 'Bot halted (DEMO)',
    });
  }),

  // ── Resume bot ────────────────────────────────────────────────────────────
  http.post(`${BASE}/api/v1/bots/:id/resume`, ({ params }) => {
    const id = Number(params['id']);
    const state = botStates[id];
    if (state) {
      botStates[id] = {
        ...state,
        runtime_state: 'running',
        allowed_actions: ['stop', 'halt', 'restart'],
        halt_reason: null,
        last_heartbeat_at: new Date().toISOString(),
      };
    }
    return HttpResponse.json({
      bot_id: id,
      action: 'resume',
      status: 'RUNNING',
      message: 'Bot resumed (DEMO)',
    });
  }),

  // ── Restart bot ───────────────────────────────────────────────────────────
  http.post(`${BASE}/api/v1/bots/:id/restart`, ({ params }) => {
    const id = Number(params['id']);
    const state = botStates[id];
    if (state) {
      botStates[id] = {
        ...state,
        runtime_state: 'running',
        allowed_actions: ['stop', 'halt', 'restart'],
        restart_count: state.restart_count + 1,
        last_heartbeat_at: new Date().toISOString(),
        cycle_count: 0,
      };
    }
    return HttpResponse.json({
      bot_id: id,
      action: 'restart',
      status: 'RUNNING',
      message: 'Bot restarted (DEMO)',
    });
  }),

  // ── Cancel all orders ─────────────────────────────────────────────────────
  http.post(`${BASE}/api/v1/bots/:id/cancel-all-orders`, ({ params }) => {
    return HttpResponse.json({
      bot_id: Number(params['id']),
      cancelled: 0,
      message: 'No open orders to cancel (DEMO)',
    });
  }),

  // ── Bot equity ────────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/bots/:id/equity`, ({ params }) => {
    const id = Number(params['id']);
    const items = BOT_EQUITIES[id] ?? [];
    return HttpResponse.json({ items, total: items.length, limit: 50, offset: 0 });
  }),

  // ── Bot PnL ───────────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/bots/:id/pnl`, ({ params }) => {
    const id = Number(params['id']);
    const items = BOT_PNL[id] ?? [];
    return HttpResponse.json({ items, total: items.length, limit: 30, offset: 0 });
  }),

  // ── Bot positions ─────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/bots/:id/positions`, ({ params }) => {
    const id = Number(params['id']);
    const items = DEMO_POSITIONS.filter((p) => p.bot_id === id);
    return HttpResponse.json({ items, total: items.length, limit: 50, offset: 0 });
  }),

  // ── Bot portfolio summary ─────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/bots/:id/portfolio`, ({ params }) => {
    const id = Number(params['id']);
    return HttpResponse.json(DEMO_BOT_PORTFOLIOS[id] ?? DEMO_BOT_PORTFOLIOS[1]);
  }),

  // ── Bot summary ───────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/bots/:id/summary`, ({ params }) => {
    const id = Number(params['id']);
    return HttpResponse.json(DEMO_BOT_SUMMARIES[id] ?? DEMO_BOT_SUMMARIES[1]);
  }),

  // ── Bot signals ───────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/bots/:id/signals`, ({ params }) => {
    const id = Number(params['id']);
    const signals = [
      {
        id: 1,
        bot_id: id,
        signal_type: 'RSI_OVERSOLD',
        symbol: 'BTC/USDT',
        direction: 'BUY',
        strength: 0.82,
        price: '67240.50',
        created_at: new Date(Date.now() - 6 * 3600 * 1000).toISOString(),
        acted_on: true,
      },
      {
        id: 2,
        bot_id: id,
        signal_type: 'RSI_OVERSOLD',
        symbol: 'BTC/USDT',
        direction: 'BUY',
        strength: 0.74,
        price: '66890.00',
        created_at: new Date(Date.now() - 14 * 3600 * 1000).toISOString(),
        acted_on: true,
      },
    ];
    return HttpResponse.json({ items: signals, total: signals.length, limit: 50, offset: 0 });
  }),

  // ── Bot events ────────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/bots/:id/events`, ({ params }) => {
    const id = Number(params['id']);
    const events = [
      {
        id: 1,
        bot_id: id,
        event_type: 'BOT_STARTED',
        payload: { message: 'Bot started' },
        created_at: new Date(Date.now() - 3 * 24 * 3600 * 1000).toISOString(),
      },
      {
        id: 2,
        bot_id: id,
        event_type: 'ORDER_FILLED',
        payload: { symbol: 'BTC/USDT', side: 'BUY', price: '67240.50' },
        created_at: new Date(Date.now() - 6 * 3600 * 1000).toISOString(),
      },
    ];
    return HttpResponse.json({ items: events, total: events.length, limit: 50, offset: 0 });
  }),

  // ── Bot risk log ──────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/bots/:id/risk-log`, ({ params }) => {
    const id = Number(params['id']);
    const entries =
      id === 3
        ? [
            {
              id: 1,
              bot_id: id,
              level: 'L3',
              rule: 'DAILY_LOSS_LIMIT',
              message: 'Daily loss exceeded: -$350.40 (-2.3%)',
              triggered_at: new Date(Date.now() - 50 * 3600 * 1000).toISOString(),
              action_taken: 'BOT_HALTED',
            },
          ]
        : [];
    return HttpResponse.json({ items: entries, total: entries.length, limit: 50, offset: 0 });
  }),

  // ── Bot resolved config ───────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/bots/:id/config`, ({ params }) => {
    const id = Number(params['id']);
    const bot = getBotById(id);
    return HttpResponse.json({
      bot_id: id,
      checksum: 'demo-checksum-abc123',
      config: bot?.config ?? {},
      resolved_at: new Date().toISOString(),
    });
  }),

  // ── Bot config preview ────────────────────────────────────────────────────
  http.post(`${BASE}/api/v1/bots/:id/config/preview`, async ({ request }) => {
    const body = (await request.json()) as Record<string, unknown>;
    return HttpResponse.json({
      config: body,
      valid: true,
      warnings: [],
      preview_id: 'demo-preview-001',
    });
  }),

  // ── Bot config history ────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/bots/:id/config-history`, () => {
    return HttpResponse.json({ items: [], total: 0, limit: 20, offset: 0 });
  }),

  // ── Bot analytics ─────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/bots/:id/analytics`, ({ params }) => {
    const id = Number(params['id']);
    return HttpResponse.json({
      bot_id: id,
      period: '30d',
      avg_trade_duration_minutes: 210.4,
      best_day: { date: '2026-02-15', pnl: '287.40' },
      worst_day: { date: '2026-02-08', pnl: '-142.30' },
      signal_accuracy: 0.647,
      avg_slippage_bps: 3.2,
    });
  }),

  // ── Bot execution quality ─────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/bots/:id/execution-quality`, ({ params }) => {
    const id = Number(params['id']);
    return HttpResponse.json({
      bot_id: id,
      avg_fill_time_ms: 127,
      fill_rate: 0.95,
      avg_slippage_bps: 3.2,
      rejected_orders: 0,
      partially_filled: 1,
    });
  }),

  // ── Reconciliation ────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/bots/:id/reconciliation`, () => {
    return HttpResponse.json({
      status: 'clean',
      last_reconciled_at: new Date().toISOString(),
      discrepancies: [],
    });
  }),
];
